import { useCallback, useRef, useState } from 'react';
import { ModalOverlay } from '../components/ModalOverlay';
import { confirmWordMatches } from '../components/confirm-word';
import { useModalA11y } from '../components/useModalA11y';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import type { ResetLibraryResult } from '@xphoto/shared';
import { useI18n } from '../i18n';
import { interpolate } from '../gallery/format';
import { resetLibrary } from './admin-api';

/**
 * Modale d'avertissement LOURDE de réinitialisation d'une bibliothèque (superadmin).
 *
 * Vraie boîte de dialogue courte (comme `BugReportModal`) : `role="dialog"` +
 * `aria-modal`, focus initial/restauré, verrou de défilement, Échap ferme. Le
 * caller l'enveloppe dans un `<Portal>` (l'en-tête porte `backdrop-blur`).
 *
 * SÛRETÉ : la modale explique en clair ce qui est SUPPRIMÉ (index médias +
 * vignettes) et ce qui est PRÉSERVÉ (fichiers originaux + EXIF disque, comptes,
 * bibliothèque, accès), et exige une CONFIRMATION ACTIVE : le bouton reste
 * désactivé tant que l'utilisateur n'a pas recopié le mot de confirmation.
 */
interface ResetLibraryModalProps {
  libraryId: string;
  onClose: () => void;
  /** Appelé après un reset réussi (compteurs), p.ex. pour rafraîchir l'écran. */
  onDone?: (result: ResetLibraryResult) => void;
}

export function ResetLibraryModal({
  libraryId,
  onClose,
  onDone,
}: ResetLibraryModalProps): React.JSX.Element {
  const { t, locale } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  useModalA11y({ dialogRef, initialFocusRef: inputRef, onEscape: !busy ? onClose : undefined });
  const [error, setError] = useState(false);
  const [result, setResult] = useState<ResetLibraryResult | null>(null);

  // Mot de confirmation à recopier (localisé). Comparaison INSENSIBLE aux accents, à la
  // casse et aux espaces — la règle et sa raison (clavier suisse) sont dans
  // `components/confirm-word.ts`, partagée avec la suppression de dossier.
  const confirmWord = t('admin.reset.confirmWord');
  const matches = confirmWordMatches(typed, confirmWord);

  // Verrou de défilement + focus initial (champ de confirmation) + restauration.

  // Échap ferme (sauf pendant l'appel réseau : on n'interrompt pas une action en cours).

  const onConfirm = useCallback(async () => {
    if (!matches || busy) return;
    setBusy(true);
    setError(false);
    try {
      const res = await resetLibrary(libraryId);
      setResult(res);
      onDone?.(res);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }, [matches, busy, libraryId, onDone]);

  const done = result !== null;

  return (
    <ModalOverlay onDismiss={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('admin.reset.modalTitle')}
        className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-surface text-fg shadow-xl"
      >
        {/* En-tête. */}
        <div className="flex items-center justify-between gap-2 border-b border-border p-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
            <ExclamationTriangleIcon className="h-6 w-6 text-danger" aria-hidden="true" />
            {t('admin.reset.modalTitle')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            title={t('admin.reset.close')}
            aria-label={t('admin.reset.close')}
            className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
          >
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Corps défilant. */}
        <div className="flex min-h-0 flex-col gap-4 overflow-y-auto p-4">
          {done ? (
            <p
              role="status"
              className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-fg"
            >
              <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
              {interpolate(
                t('admin.reset.done'),
                {
                  assets: result.deletedAssets,
                  thumbnails: result.deletedThumbnails,
                },
                locale,
              )}
            </p>
          ) : (
            <>
              {/* Ce qui sera supprimé (danger). */}
              <p className="flex items-start gap-2 rounded-md border border-danger/40 bg-surface-2 p-3 text-sm text-fg">
                <ExclamationTriangleIcon
                  className="mt-0.5 h-5 w-5 shrink-0 text-danger"
                  aria-hidden="true"
                />
                <span>
                  <span className="font-medium">{t('admin.reset.modalWarning')}</span>
                  <span className="mt-1 block text-muted">{t('admin.reset.deletes')}</span>
                </span>
              </p>

              {/* Ce qui est préservé (sûreté). */}
              <p className="flex items-start gap-2 rounded-md bg-surface-2 p-3 text-sm text-fg">
                <ShieldCheckIcon
                  className="mt-0.5 h-5 w-5 shrink-0 text-brand"
                  aria-hidden="true"
                />
                <span>
                  <span className="block">{t('admin.reset.modalSafe')}</span>
                  <span className="mt-1 block text-muted">{t('admin.reset.preserves')}</span>
                </span>
              </p>

              {/* Confirmation active : recopier le mot. */}
              <label className="flex flex-col gap-1">
                <span className="text-sm font-medium text-fg">
                  {interpolate(t('admin.reset.confirmLabel'), { word: confirmWord })}
                </span>
                <input
                  ref={inputRef}
                  type="text"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  disabled={busy}
                  autoComplete="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder={t('admin.reset.confirmPlaceholder')}
                  aria-invalid={error}
                  className="h-11 rounded-md border border-border bg-surface px-3 text-sm text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:opacity-50"
                />
              </label>

              {error && (
                <p role="alert" className="inline-flex items-center gap-1 text-sm text-danger">
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
                  {t('admin.reset.error')}
                </p>
              )}
            </>
          )}
        </div>

        {/* Actions. */}
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border p-4">
          {done ? (
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {t('admin.reset.close')}
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-50"
              >
                {t('admin.reset.cancel')}
              </button>
              <button
                type="button"
                onClick={() => void onConfirm()}
                disabled={!matches || busy}
                className="inline-flex items-center gap-2 rounded-md bg-danger px-4 py-2 text-sm font-medium text-danger-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy && <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />}
                {busy ? t('admin.reset.resetting') : t('admin.reset.confirm')}
              </button>
            </>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
